import React, { useCallback, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/fontawesome-free-solid";
import Dropdown from "../../../shared/components/common/Dropdown";
import ExportModal from "./ExportModal";
import FilterIcon from "../../../assets/icons/Filter.svg";
import PrintIcon from "../../../assets/icons/PrintIcon.svg";
import DownloadIcon from "../../../assets/icons/DownloadIcon.svg";
import FullViewIcon from "../../../assets/icons/FullViewIcon.svg";
import NormalViewIcon from "../../../assets/icons/NormalViewIcon.svg";

const LogsToolbar = ({
  searchQuery,
  setSearchQuery,
  spreadTable,
  setSpreadTable,
  setShowFilterModal,
  statusFilter,
  setStatusFilter,
  statusOptions,
  displayLogs,
  columns,
}) => {
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportType, setExportType] = useState("csv");
  const toolbarRootRef = useRef(null);

  const getTableElement = useCallback(() => {
    const toolbarRoot = toolbarRootRef.current;
    const scopedContainer = toolbarRoot?.closest(
      ".dashboard-table-area, .logs-table-area",
    );

    if (scopedContainer) {
      const table = scopedContainer.querySelector("table");
      if (table) return table;
    }

    return document.querySelector(".logs-table-area table");
  }, []);

  const handlePrint = () => {
    const table = getTableElement();
    if (table) {
      const tableClone = table.cloneNode(true);
      const ths = tableClone.querySelectorAll("th");
      ths.forEach((th) => {
        th.querySelectorAll(
          "svg, i, span[class*=icon], .fa, .svg-inline--fa",
        ).forEach((el) => el.remove());
      });
      const printWindow = window.open("", "", "width=900,height=700");
      printWindow.document.write("<html><head><title>Print Table</title>");
      printWindow.document.write(
        "<style>body{font-family:sans-serif;}table{width:100%;border-collapse:collapse;}th,td{border:1px solid #ccc;padding:8px;}</style>",
      );
      printWindow.document.write("</head><body>");
      printWindow.document.write(
        "<table>" +
          tableClone.tHead.outerHTML +
          tableClone.tBodies[0].outerHTML +
          "</table>",
      );
      printWindow.document.write("</body></html>");
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    }
  };

  return (
    <>
      <div
        className="p-4 xl:p-3 bg-white border-b border-line flex sm:flex-row flex-col justify-between sm:items-center md:gap-0 gap-3"
        ref={toolbarRootRef}
      >
        {/* Search */}
        <div className="relative">
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute left-4 xl:left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm xl:text-xs"
          />
          <input
            type="text"
            placeholder="Search by user, subject, number"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-11 xl:pl-10 pr-4 xl:pr-3 py-2.5 xl:py-2 border border-content rounded-lg text-sm xl:text-xs w-full md:w-72 xl:w-64 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-content"
          />
        </div>

        {/* Toolbar Actions */}
        <div className="hidden sm:flex items-center gap-2 md:gap-3 xl:gap-2">
          <div className="h-6 w-[1px] bg-surface mx-2"></div>

          <button
            className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"
            onClick={() => setShowFilterModal(true)}
          >
            <img src={FilterIcon} alt="Filter" className="w-4 h-4" loading="eager"/>
          </button>

          <button
            className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"
            onClick={handlePrint}
          >
            <img src={PrintIcon} alt="Print" className="w-4 h-4" loading="eager"/>
          </button>

          <div className="relative">
            <button
              className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 relative"
              onClick={() => setShowExportModal((v) => !v)}
            >
              <img src={DownloadIcon} alt="Download" className="w-4 h-4" loading="eager"/>
            </button>
            <ExportModal
              isOpen={showExportModal}
              onClose={() => setShowExportModal(false)}
              exportType={exportType}
              setExportType={setExportType}
              displayLogs={displayLogs}
              columns={columns}
              getTableElement={getTableElement}
            />
          </div>

          {/* Spread Table (Zoom Out) */}
          <button
            className={`p-2 hover:bg-gray-100 rounded-lg text-gray-500 ${
              spreadTable ? "bg-gray-200" : ""
            }`}
            onClick={() => setSpreadTable(!spreadTable)}
            aria-label="Spread Table (Zoom Out)"
          >
            {!spreadTable ? (
              <img
                src={FullViewIcon}
                alt="Zoom Out Table"
                className="w-4 h-4"
                loading="eager"
              />
            ) : (
              <img
                src={NormalViewIcon}
                alt="Zoom Out Table"
                className="w-auto h-auto"
                loading="eager"
              />
            )}
          </button>

          <div className="h-6 w-[1px] bg-surface mr-2"></div>

          <Dropdown
            options={statusOptions}
            value={statusFilter}
            onChange={setStatusFilter}
            label="All Status"
          />
          {/* <Dropdown
          options={securityOptions}
          value={securityFilter}
          onChange={setSecurityFilter}
          label="All Security"
          /> */}
        </div>
        {/*  */}
        <div className="sm:hidden flex max-[500px]:flex-col flex-row max-[500px]:items-start items-center gap-3 xl:gap-2">
          <div className="sm:hidden flex max-sm:justify-between items-center gap-2 xl:gap-2 w-full">
            <div className="h-6 w-[1px] bg-surface mx-2"></div>

            <button
              className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"
              onClick={() => setShowFilterModal(true)}
            >
              <img src={FilterIcon} alt="Filter" className="w-4 h-4" loading="eager"/>
            </button>

            <button
              className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"
              onClick={handlePrint}
            >
              <img src={PrintIcon} alt="Print" className="w-4 h-4" loading="eager"/>
            </button>

            <div className="relative">
              <button
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 relative"
                onClick={() => setShowExportModal((v) => !v)}
              >
                <img src={DownloadIcon} alt="Download" className="w-4 h-4" loading="eager"/>
              </button>
              <ExportModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                exportType={exportType}
                setExportType={setExportType}
                displayLogs={displayLogs}
                columns={columns}
                getTableElement={getTableElement}
              />
            </div>

            {/* Spread Table (Zoom Out) */}
            <button
              className={`p-2 hover:bg-gray-100 rounded-lg text-gray-500 ${
                spreadTable ? "bg-gray-200" : ""
              }`}
              onClick={() => setSpreadTable(!spreadTable)}
              aria-label="Spread Table (Zoom Out)"
            >
              {!spreadTable ? (
                <img
                  src={FullViewIcon}
                  alt="Zoom Out Table"
                  className="w-4 h-4"
                  loading="eager"
                />
              ) : (
                <img
                  src={NormalViewIcon}
                  alt="Zoom Out Table"
                  className="w-auto h-auto"
                  loading="eager"
                />
              )}
            </button>

            <div className="h-6 w-[1px] bg-surface mr-2"></div>
          </div>

          <Dropdown
            options={statusOptions}
            value={statusFilter}
            onChange={setStatusFilter}
            label="All Status"
          />
        </div>
      </div>
    </>
  );
};

export default LogsToolbar;
